import { useEffect, useState, useCallback, useRef } from "react";
import {
  FiSearch,
  FiMessageSquare,
  FiSend,
  FiX,
  FiCheckCircle,
  FiAlertCircle,
} from "react-icons/fi";
import { makeRequestAuth, uploadFiles } from "../../_config/api";
import Spinner from "../../utils/Loader";
import { io } from "socket.io-client";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";

function AllSupportTickets() {
  const socketRef = useRef(null);
  const bottomRef = useRef(null);

  const [ticketList, setTicketList] = useState([]);
  const [admin, setAdmin] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [chatModal, setChatModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState("");

  const [resolveModal, setResolveModal] = useState(false);
  const [resolutionReason, setResolutionReason] = useState("");

  const [closeModal, setCloseModal] = useState(false);
  const [closeReason, setCloseReason] = useState("");

  const [imageUrl, setImageUrl] = useState("");
  const [imagePreview, setImagePreview] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  console.log("messages", messages);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);

      const result = await uploadFiles({
        files: [file],
        endpoint: "/uploadImage",
        fieldName: "file",
        isMultiple: false,
      });

      const imageUrl = result?.data?.imageUrl;

      if (imageUrl) {
        setImageUrl(imageUrl);
        setImagePreview(imageUrl);
        showSuccessToast("Image uploaded successfully!");
      } else {
        showErrorToast("Image upload failed");
      }
    } catch (error) {
      console.error("Image upload failed:", error);
      showErrorToast("Error uploading image");
    } finally {
      setIsUploading(false);
    }
  };

  // ===============================
  // LOGIC FUNCTIONS (UNCHANGED)
  // ===============================
  const resolveTicket = (ticket) => {
    setSelectedTicket(ticket);
    setResolveModal(true);
  };

  const closeTicket = (ticket) => {
    setSelectedTicket(ticket);
    setCloseModal(true);
  };

  const confirmClose = () => {
    if (!closeReason.trim()) return;
    socketRef.current.emit(
      "closeSupportTicket",
      {
        ticketId: selectedTicket._id,
        closeReason,
      },
      (res) => {
        if (res?.status) {
          setTicketList((prev) =>
            prev.map((t) => (t._id === selectedTicket._id ? res.data : t)),
          );
          setCloseModal(false);
          setCloseReason("");
        } else {
          console.log(res.message);
        }
      },
    );
  };

  useEffect(() => {
    if (!socketRef.current) return;
    const handleClosed = (ticket) => {
      setTicketList((prev) =>
        prev.map((t) => (t._id === ticket._id ? ticket : t)),
      );
    };
    socketRef.current.on("ticketClosed", handleClosed);
    return () => socketRef.current.off("ticketClosed", handleClosed);
  }, []);

  const confirmResolve = () => {
    if (!resolutionReason.trim()) return;
    socketRef.current.emit(
      "resolveSupportTicket",
      {
        ticketId: selectedTicket._id,
        resolutionReason,
      },
      (res) => {
        if (res?.status) {
          setTicketList((prev) =>
            prev.map((t) => (t._id === selectedTicket._id ? res.data : t)),
          );
          setResolveModal(false);
          setResolutionReason("");
        } else {
          console.log(res.message);
        }
      },
    );
  };

  useEffect(() => {
    if (!socketRef.current) return;
    const handleResolved = (ticket) => {
      setTicketList((prev) =>
        prev.map((t) => (t._id === ticket._id ? ticket : t)),
      );
    };
    socketRef.current.on("ticketResolved", handleResolved);
    return () => socketRef.current.off("ticketResolved", handleResolved);
  }, []);

  useEffect(() => {
    socketRef.current = io("https://api.coinswitchpay.in");
    return () => socketRef.current.disconnect();
  }, []);

  const fetchTickets = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await makeRequestAuth("get", "/admin/getSupportTicketList");
      const resAdmin = await makeRequestAuth("get", "/admin/getProfile");
      setTicketList(res?.data || []);
      setAdmin(resAdmin?.data || []);
    } catch (err) {
      console.log(err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const filteredList = ticketList.filter(
    (item) =>
      item.reasonPath
        ?.join(" ")
        .toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      item.userId?.email?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const openChat = async (ticket) => {
    setSelectedTicket(ticket);
    setChatModal(true);
    socketRef.current.emit(
      "joinSupportRoom",
      { ticketId: ticket._id },
      (res) => {
        console.log("Joined room:", res);
      },
    );
    try {
      const res = await makeRequestAuth(
        "get",
        `/admin/getTicketMessages?ticketId=${ticket._id}`,
      );
      if (res?.data) setMessages(res.data);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (!socketRef.current) return;
    const handleReceive = (msg) => {
      if (msg.ticketId !== selectedTicket?._id) return;

      setMessages((prev) => {
        const exists = prev.some(
          (m) =>
            m.message === msg.message &&
            m.senderType === msg.senderType &&
            JSON.stringify(m.attachments) === JSON.stringify(msg.attachments),
        );

        if (exists) return prev;

        return [...prev, msg];
      });
    };
    socketRef.current.on("receiveSupportMessage", handleReceive);
    return () => socketRef.current.off("receiveSupportMessage", handleReceive);
  }, [selectedTicket]);

  const sendMessage = () => {
    if ((!messageInput.trim() && !imageUrl) || !selectedTicket) return;

    const payload = {
      ticketId: selectedTicket._id,
      senderType: "Admin",
      senderId: admin._id,
      message: messageInput || "",
      attachments: imageUrl
        ? [
            {
              url: imageUrl,
              type: "image",
            },
          ]
        : [],
    };

    socketRef.current.emit("sendSupportMessage", payload);

    setMessages((prev) => [...prev, payload]);

    setMessageInput("");
    setImageUrl("");
    setImagePreview("");
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  if (isLoading && ticketList.length === 0) return <Spinner />;

  return (
    <div className="min-h-screen bg-[#F9FAFB] dark:bg-[#0F172A] p-6 text-slate-900 dark:text-slate-100 transition-colors duration-300">
      {/* MAIN CONTAINER */}
      <div className="max-w-7xl mx-auto bg-white dark:bg-[#1E293B] rounded-3xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        {/* HEADER */}
        <div className="p-8 border-b border-slate-100 dark:border-slate-800 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-extrabold tracking-tight">
              Support Tickets
            </h2>
            <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
              Manage and resolve user inquiries effectively.
            </p>
          </div>

          <div className="relative group">
            <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-black dark:group-focus-within:text-white transition-colors" />
            <input
              type="text"
              placeholder="Search by email or reason..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full md:w-80 pl-11 pr-4 py-2.5 bg-slate-50 dark:bg-[#0F172A] border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-black dark:focus:ring-white outline-none transition-all text-sm"
            />
          </div>
        </div>

        {/* TABLE SECTION */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/50 dark:bg-[#161E2E] text-slate-500 dark:text-slate-400 text-xs uppercase font-semibold tracking-wider">
                <th className="py-4 px-8">ID</th>
                <th className="py-4 px-6">User Account</th>
                <th className="py-4 px-6">Reason Path</th>
                <th className="py-4 px-6 text-center">Status</th>
                <th className="py-4 px-8 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {filteredList.length === 0 ? (
                <tr>
                  <td
                    colSpan="5"
                    className="text-center py-20 text-slate-400 italic"
                  >
                    No tickets found matching your criteria.
                  </td>
                </tr>
              ) : (
                filteredList.map((item, index) => (
                  <tr
                    key={item._id}
                    className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40 transition-colors group"
                  >
                    <td className="py-5 px-8 text-sm font-medium text-slate-400">
                      #{index + 1}
                    </td>
                    <td className="py-5 px-6">
                      <div className="font-semibold text-sm">
                        {item.userId?.email || "Anonymous"}
                      </div>
                    </td>
                    <td className="py-5 px-6">
                      <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
                        {item.reasonPath?.map((r, i) => (
                          <span key={i} className="flex items-center">
                            {r}{" "}
                            {i < item.reasonPath.length - 1 && (
                              <span className="mx-1 opacity-30">/</span>
                            )}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="py-5 px-6 text-center">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize shadow-sm
                        ${
                          item.status === "resolved"
                            ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400 border border-emerald-200/50 dark:border-emerald-500/20"
                            : "bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400 border border-amber-200/50 dark:border-amber-500/20"
                        }`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full mr-1.5 ${item.status === "resolved" ? "bg-emerald-500" : "bg-amber-500 animate-pulse"}`}
                        ></span>
                        {item.status}
                      </span>
                    </td>
                    <td className="py-5 px-8 text-right">
                      <div className="flex justify-end items-center gap-3">
                        <button
                          onClick={() => openChat(item)}
                          title="Open Chat"
                          className="p-2 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 transition-all"
                        >
                          <FiMessageSquare size={19} />
                        </button>
                        {item.status === "open" && (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => resolveTicket(item)}
                              className="px-3 py-1.5 text-xs font-bold bg-black dark:bg-white text-white dark:text-black rounded-lg hover:opacity-80 transition-opacity"
                            >
                              Resolve
                            </button>
                            <button
                              onClick={() => closeTicket(item)}
                              className="px-3 py-1.5 text-xs font-bold border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-red-50 hover:text-red-600 transition-all"
                            >
                              Close
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* RESOLVE MODAL */}
      {resolveModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex justify-center items-center z-[60] p-4">
          <div className="bg-white dark:bg-[#1E293B] w-full max-w-md rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800 animate-in fade-in zoom-in duration-200">
            <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center">
              <h3 className="text-lg font-bold flex items-center gap-2 italic">
                <FiCheckCircle className="text-emerald-500" /> Resolve Ticket
              </h3>
              <button
                onClick={() => setResolveModal(false)}
                className="text-slate-400 hover:text-black dark:hover:text-white"
              >
                <FiX size={20} />
              </button>
            </div>
            <div className="p-6">
              <label className="text-xs font-bold uppercase text-slate-400 mb-2 block tracking-widest">
                Resolution Notes
              </label>
              <textarea
                placeholder="How was this resolved?"
                value={resolutionReason}
                onChange={(e) => setResolutionReason(e.target.value)}
                className="w-full bg-slate-50 dark:bg-[#0F172A] border border-slate-200 dark:border-slate-700 rounded-xl p-4 min-h-[120px] focus:ring-2 focus:ring-black dark:focus:ring-white outline-none text-sm"
              />
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setResolveModal(false)}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold border border-slate-200 dark:border-slate-700 rounded-xl"
                >
                  Discard
                </button>
                <button
                  onClick={confirmResolve}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold bg-black dark:bg-white text-white dark:text-black rounded-xl hover:opacity-90"
                >
                  Mark Resolved
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CLOSE MODAL */}
      {closeModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex justify-center items-center z-[60] p-4">
          <div className="bg-white dark:bg-[#1E293B] w-full max-w-md rounded-3xl shadow-2xl border border-slate-200 dark:border-slate-800">
            <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center">
              <h3 className="text-lg font-bold flex items-center gap-2 italic">
                <FiAlertCircle className="text-red-500" /> Close Ticket
              </h3>
              <button
                onClick={() => setCloseModal(false)}
                className="text-slate-400 hover:text-black dark:hover:text-white"
              >
                <FiX size={20} />
              </button>
            </div>
            <div className="p-6">
              <label className="text-xs font-bold uppercase text-slate-400 mb-2 block tracking-widest">
                Reason for Closing
              </label>
              <textarea
                placeholder="Reason for closing (e.g. Inactivity, Duplicate)..."
                value={closeReason}
                onChange={(e) => setCloseReason(e.target.value)}
                className="w-full bg-slate-50 dark:bg-[#0F172A] border border-slate-200 dark:border-slate-700 rounded-xl p-4 min-h-[120px] focus:ring-2 focus:ring-red-500 outline-none text-sm"
              />
              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setCloseModal(false)}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold border border-slate-200 dark:border-slate-700 rounded-xl"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmClose}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold bg-red-600 text-white rounded-xl hover:bg-red-700"
                >
                  Confirm Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CHAT MODAL */}
      {chatModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex justify-end z-[60]">
          <div className="bg-white dark:bg-[#1E293B] w-full max-w-md h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
            {/* CHAT HEADER */}
            <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-white dark:bg-[#1E293B] sticky top-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center font-bold text-slate-400 uppercase">
                  {selectedTicket?.userId?.email?.charAt(0) || "U"}
                </div>
                <div>
                  <h3 className="font-bold text-sm leading-none">
                    {selectedTicket?.userId?.email}
                  </h3>
                  <span className="text-[10px] text-slate-400 font-mono mt-1 block">
                    ID: {selectedTicket?._id}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setChatModal(false)}
                className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full text-slate-400"
              >
                <FiX size={20} />
              </button>
            </div>

            {/* MESSAGES AREA */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-slate-50/30 dark:bg-[#0F172A]/30">
              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex ${msg.senderType === "Admin" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm shadow-sm ${
                      msg.senderType === "Admin"
                        ? "bg-black dark:bg-white text-white dark:text-black rounded-tr-none"
                        : "bg-white dark:bg-[#334155] text-slate-800 dark:text-slate-100 border border-slate-100 dark:border-slate-700 rounded-tl-none"
                    }`}
                  >
                    {/* whitespace-pre-wrap: Line breaks preserve karne ke liye */}
                    {/* break-words: Lambe words ko todne ke liye */}
                    <div className="flex flex-col gap-2 min-w-0">
                      {msg.message && (
                        <span className="whitespace-pre-wrap break-words leading-relaxed">
                          {msg.message}
                        </span>
                      )}

                      {msg.attachments?.map((att, index) => (
                        <img
                          key={index}
                          src={typeof att === "string" ? att : att.url}
                          alt="attachment"
                          className="max-w-full h-auto rounded-lg mt-1"
                        />
                      ))}
                    </div>
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            {/* CHAT INPUT */}
            {selectedTicket.status === "open" ? (
              <div className="p-6 border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-[#1E293B]">
                <div className="relative flex items-center gap-2">
                  {imagePreview && (
                    <div className="p-2">
                      <img
                        src={imagePreview}
                        className="w-20 h-20 rounded-lg object-cover"
                        alt="preview"
                      />
                    </div>
                  )}

                  <label className="cursor-pointer">
                    📎
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageUpload}
                    />
                  </label>

                  <input
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && sendMessage()}
                    placeholder="Type a message..."
                    className="flex-1 px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-[#0F172A] outline-none focus:ring-2 focus:ring-black dark:focus:ring-white text-sm"
                  />
                  <button
                    onClick={sendMessage}
                    className="h-11 w-11 bg-black dark:bg-white text-white dark:text-black rounded-xl flex items-center justify-center hover:opacity-80 transition-all shrink-0 shadow-lg"
                  >
                    <FiSend size={18} />
                  </button>
                </div>
              </div>
            ) : selectedTicket.status === "closed" ? (
              <div className="p-4 bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 text-center text-xs font-bold uppercase tracking-widest">
                This ticket is closed.
              </div>
            ) : (
              <div className="p-4 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-center text-xs font-bold uppercase tracking-widest">
                This ticket is resolved.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AllSupportTickets;
